import { z } from "zod";
import { CONSENT_VERSION } from "./consent.js";
import { normalizeDigits } from "./digits.js";
import {
  AUDIO_MAX_BYTES,
  CONTACT_LINKS_MAX,
  DISPLAY_NAME_MAX,
  LINK_MAX,
  MESSAGE_MAX,
  NOTE_MAX,
  STORY_TEXT_MAX,
  STORY_TEXT_MIN,
  STORY_TITLE_MAX,
  SUPPORT_MESSAGE_MAX,
  VIDEO_TUS_MAX_BYTES,
} from "./limits.js";
import { PROMPT_KEYS } from "./prompts.js";
import {
  AMOUNT_INTENTS,
  CLIP_FORMATS,
  CONTACT_LANGUAGES,
  CONTACT_STATUSES,
  CONTACT_TOPICS,
  IDENTITY_PREFERENCES,
  LOCALES,
  NEWSLETTER_SOURCES,
  SHARE_PLATFORMS,
  SUBMISSION_KINDS,
  SUBMISSION_STATUSES,
} from "./types.js";

/**
 * Request schemas — the wire contract between app/ and server/. Strict on
 * INPUT only: what the server sends back is typed in types.ts, never parsed
 * through these (input-validation schema ≠ output schema).
 */

// ---------------------------------------------------------------------------
// Primitives
// ---------------------------------------------------------------------------

const trimmed = (max: number) => z.string().trim().max(max);

/** Emails are lower-cased and digit-normalised (Persian keyboards type ۰-۹). */
export const emailSchema = z.preprocess(
  (v) => (typeof v === "string" ? normalizeDigits(v).trim().toLowerCase() : v),
  z.email().max(254),
);

/** https only; digits normalised so a Persian-typed link validates. */
export const httpsUrlSchema = z.preprocess(
  (v) => (typeof v === "string" ? normalizeDigits(v).trim() : v),
  z
    .url()
    .max(LINK_MAX)
    .refine((u) => u.startsWith("https://"), { message: "https_required" }),
);

export const phoneSchema = z.preprocess(
  (v) => (typeof v === "string" ? normalizeDigits(v).trim() : v),
  z
    .string()
    .max(32)
    .regex(/^\+?[0-9 ()-]{5,31}$/, { message: "phone_invalid" }),
);

export const localeSchema = z.enum(LOCALES);

/**
 * Anti-spam fields every public form carries. `fax` is the honeypot: a name
 * no autofill heuristic fills, hidden by CSS. A non-empty value does NOT
 * discard the submission — it quarantines it for admin review. Silently
 * losing a real story is the worst failure this site can have.
 */
const antiSpamFields = {
  fax: z.string().max(200).optional().default(""),
  formToken: z.string().min(16).max(512),
};

// ---------------------------------------------------------------------------
// Story submission
// ---------------------------------------------------------------------------

export const consentsSchema = z.object({
  /** Required — literal true. Art 6(1)(a) + Art 9(2)(a) explicit consent. */
  storageReview: z.literal(true),
  ageConfirmed: z.literal(true),
  filmLicense: z.boolean(),
  webExcerpts: z.boolean(),
  futureContact: z.boolean(),
  updates: z.boolean(),
});
export type Consents = z.infer<typeof consentsSchema>;

export const submissionCreateSchema = z
  .object({
    kind: z.enum(SUBMISSION_KINDS),
    storyTitle: trimmed(STORY_TITLE_MAX).optional(),
    storyText: z.string().trim().max(STORY_TEXT_MAX).optional(),
    /** Short note beside an audio/video/link submission. */
    note: trimmed(NOTE_MAX).optional(),
    linkUrl: httpsUrlSchema.optional(),
    mediaId: z.uuid().optional(),
    promptKey: z.enum(PROMPT_KEYS).optional(),
    identityPreference: z.enum(IDENTITY_PREFERENCES),
    displayName: trimmed(DISPLAY_NAME_MAX).optional(),
    email: emailSchema.optional(),
    locale: localeSchema,
    consentVersion: z.literal(CONSENT_VERSION),
    consents: consentsSchema,
    ...antiSpamFields,
  })
  .superRefine((v, ctx) => {
    const issue = (path: string, message: string) =>
      ctx.addIssue({ code: "custom", path: [path], message });

    if (v.kind === "text") {
      if (!v.storyText || v.storyText.length < STORY_TEXT_MIN) issue("storyText", "story_required");
    }
    if (v.kind === "link" && !v.linkUrl) issue("linkUrl", "link_required");
    if ((v.kind === "video" || v.kind === "audio") && !v.mediaId) issue("mediaId", "media_required");

    if (v.identityPreference !== "anonymous" && !v.displayName) {
      issue("displayName", "name_required");
    }
    if (v.identityPreference === "anonymous" && v.displayName) {
      issue("displayName", "anonymous_has_no_name");
    }
    // Both of these are promises to reach the contributor; neither can be kept without an address.
    if ((v.consents.futureContact || v.consents.updates) && !v.email) {
      issue("email", "email_required_for_contact");
    }
  });
export type SubmissionCreate = z.infer<typeof submissionCreateSchema>;

export const withdrawSchema = z
  .object({
    token: z.string().min(16).max(128),
    action: z.enum(["withdraw", "update_identity"]),
    identityPreference: z.enum(IDENTITY_PREFERENCES).optional(),
    displayName: trimmed(DISPLAY_NAME_MAX).optional(),
  })
  .superRefine((v, ctx) => {
    if (v.action === "update_identity") {
      if (!v.identityPreference) {
        ctx.addIssue({ code: "custom", path: ["identityPreference"], message: "required" });
      } else if (v.identityPreference !== "anonymous" && !v.displayName) {
        ctx.addIssue({ code: "custom", path: ["displayName"], message: "name_required" });
      }
    }
  });
export type WithdrawRequest = z.infer<typeof withdrawSchema>;

/** Sent with a tus upload's metadata / the audio multipart so uploads need a live form token too. */
export const uploadMetaSchema = z.object({
  kind: z.enum(["video", "audio"]),
  formToken: z.string().min(16).max(512),
  /** Client-reported size, used only to refuse early; the disk write enforces the real cap. */
  size: z.number().int().positive().max(VIDEO_TUS_MAX_BYTES).optional(),
});
export type UploadMeta = z.infer<typeof uploadMetaSchema>;

/** Per-kind byte caps the upload routes enforce; exported so the client refuses early too. */
export const UPLOAD_MAX_BYTES = { video: VIDEO_TUS_MAX_BYTES, audio: AUDIO_MAX_BYTES } as const;

// ---------------------------------------------------------------------------
// Support interest
// ---------------------------------------------------------------------------

export const supportInterestSchema = z.object({
  email: emailSchema,
  name: trimmed(DISPLAY_NAME_MAX).optional(),
  help: z.object({
    funding: z.boolean().default(false),
    screening: z.boolean().default(false),
    connect: z.boolean().default(false),
    volunteer: z.boolean().default(false),
  }),
  amountIntent: z.enum(AMOUNT_INTENTS).optional(),
  message: trimmed(SUPPORT_MESSAGE_MAX).optional(),
  /** The form's single purpose, so its own consent — never bundled. */
  consentContact: z.literal(true),
  consentUpdates: z.boolean().default(false),
  locale: localeSchema,
  ...antiSpamFields,
});
export type SupportInterest = z.infer<typeof supportInterestSchema>;

// ---------------------------------------------------------------------------
// Contact / casting
// ---------------------------------------------------------------------------

export const contactSchema = z
  .object({
    topic: z.enum(CONTACT_TOPICS),
    name: trimmed(DISPLAY_NAME_MAX).min(1),
    email: emailSchema,
    phone: phoneSchema.optional(),
    languages: z.array(z.enum(CONTACT_LANGUAGES)).max(4).optional(),
    languagesOther: trimmed(80).optional(),
    location: trimmed(120).optional(),
    links: z.array(httpsUrlSchema).max(CONTACT_LINKS_MAX).optional(),
    ageConfirmed: z.boolean().optional(),
    message: z.string().trim().min(1).max(MESSAGE_MAX),
    consentStore: z.literal(true),
    keepOnFile: z.boolean().default(false),
    locale: localeSchema,
    ...antiSpamFields,
  })
  .superRefine((v, ctx) => {
    if (v.topic === "casting" && v.ageConfirmed !== true) {
      ctx.addIssue({ code: "custom", path: ["ageConfirmed"], message: "age_required_for_casting" });
    }
  });
export type ContactMessage = z.infer<typeof contactSchema>;

export const contactStatusPatchSchema = z.object({
  status: z.enum(CONTACT_STATUSES),
});

// ---------------------------------------------------------------------------
// Newsletter (double opt-in)
// ---------------------------------------------------------------------------

export const newsletterSchema = z.object({
  email: emailSchema,
  locale: localeSchema,
  source: z.enum(NEWSLETTER_SOURCES),
  ...antiSpamFields,
});
export type NewsletterSignup = z.infer<typeof newsletterSchema>;

/** Confirmation is a POST from a button on the landing page — never a state-changing GET. */
export const newsletterTokenSchema = z.object({ token: z.string().min(16).max(128) });

// ---------------------------------------------------------------------------
// Share beacon
// ---------------------------------------------------------------------------

export const shareEventSchema = z.object({
  clipSlug: z.string().min(1).max(80),
  platform: z.enum(SHARE_PLATFORMS),
  locale: localeSchema,
});
export type ShareEvent = z.infer<typeof shareEventSchema>;

// ---------------------------------------------------------------------------
// Admin
// ---------------------------------------------------------------------------

export const adminLoginSchema = z.object({ password: z.string().min(1).max(200) });

export const submissionPatchSchema = z.object({
  status: z.enum(SUBMISSION_STATUSES).optional(),
  adminNote: z.string().trim().max(MESSAGE_MAX).nullable().optional(),
  spamFlagged: z.boolean().optional(),
});
export type SubmissionPatch = z.infer<typeof submissionPatchSchema>;

export const slugSchema = z
  .string()
  .min(1)
  .max(80)
  .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, { message: "slug_invalid" });

export const youtubeIdSchema = z.string().regex(/^[A-Za-z0-9_-]{11}$/, { message: "youtube_id_invalid" });

export const clipCreateSchema = z.object({
  slug: slugSchema,
  youtubeId: youtubeIdSchema,
  format: z.enum(CLIP_FORMATS),
  titleEn: trimmed(140).min(1),
  titleFa: trimmed(140).min(1),
  captionEn: trimmed(280).min(1),
  captionFa: trimmed(280).min(1),
  descriptionEn: trimmed(1000).optional(),
  descriptionFa: trimmed(1000).optional(),
  published: z.boolean().default(false),
});
export type ClipCreate = z.infer<typeof clipCreateSchema>;

export const clipPatchSchema = clipCreateSchema.partial();
export type ClipPatch = z.infer<typeof clipPatchSchema>;
